import { useCallback, useMemo, useState } from 'react';

import type {
  AddressSearchResultFragment,
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
  SavedSearchFieldsFragment,
} from '__generated__/athena/graphql';

import type { GeoPoint, MapLocation, MarketInfo } from '../map/types';
import {
  buildSaveSearchQuery,
  canApplySavedSearchInPlace,
  pickPathname,
  toPropertySearchQuery,
} from '../savedSearches/mapping';
import { useMarketplaceSearch } from './useMarketplaceSearch';
import { buildQueryParams, buildVerticesQueryParams, updateQueryParams } from './useSearchQueries';
import type { PropertySearchQuery } from './useSearchQueries';
import type { useSearchState } from './useSearchState';
import { buildPayloadFromQuery } from './useSearchState';

type SearchState = ReturnType<typeof useSearchState>;

interface UseMarketplaceSearchControllerArgs {
  initialSearch: MarketplacePropertySearchFragment;
  initialList: MarketplacePropertyListFragment | null;
  searchState: SearchState;
  marketInfo: MarketInfo;
}

/**
 * The marketplace search session for `/homes/[market]`: it owns the result set
 * + loading flag (via `useMarketplaceSearch`) and the location the next search
 * runs against (map polygon, selected zone, or bare market), and exposes the
 * semantic search verbs the page used to hand-roll.
 *
 * Every entry point previously repeated the same "sync URL → build payload →
 * run → reset page" dance with a copy of the location expression; centralizing
 * it here keeps those flows in one tested place and stops the page (already a
 * large component) from accreting more orchestration.
 */
export const useMarketplaceSearchController = ({
  initialSearch,
  initialList,
  searchState,
  marketInfo,
}: UseMarketplaceSearchControllerArgs) => {
  const {
    propertySearchData,
    propertyListData,
    setPropertyListData,
    isLoading,
    setIsLoading,
    hasError,
    clearError,
    runSearch,
    loadNextPage,
  } = useMarketplaceSearch(initialSearch, initialList);

  const [vertices, setVertices] = useState<GeoPoint[]>(marketInfo.shape.coordinates[0]);
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | undefined>();
  const [selectedAddress, setSelectedAddress] = useState<AddressSearchResultFragment | undefined>();

  // The location the next search runs against. `buildPayloadFromQuery` treats
  // these as mutually exclusive (vertices > zoneIds > market); zone selection
  // clears `vertices` so a chosen zone wins, otherwise the current map polygon
  // (or, when empty, the bare market) drives results.
  const currentSearchLocation: Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'> =
    useMemo(
      () => ({
        market: marketInfo.name,
        vertices,
        zoneIds: selectedLocation?.value ? [selectedLocation.value] : null,
      }),
      [marketInfo.name, vertices, selectedLocation?.value],
    );

  // Runs a search against the current location, optionally overriding filter
  // values being set in the same tick (the async filter setters wouldn't be
  // visible to `buildSearchPayload` yet). Backs both the quick and full filter
  // menus.
  const applyFilters = useCallback(
    async (filterOverride: Partial<PropertySearchQuery> = {}) => {
      const status = await runSearch(
        searchState.buildSearchPayload({ ...filterOverride, ...currentSearchLocation }),
      );
      // Only rewrite the URL / reset the page once the filtered results are on
      // screen. A failed (e.g. Athena rate-limit) or superseded fetch must not
      // leave the URL advertising filters the visible listings don't reflect;
      // the previous applied query stays in the URL so a retry is consistent.
      if (status !== 'applied') {
        return;
      }
      updateQueryParams({ ...searchState.getCanonical(), ...filterOverride });
      searchState.resetPage();
    },
    [searchState, runSearch, currentSearchLocation],
  );

  // Runs a polygon search for a freshly drawn/panned map viewport and, once its
  // results are on screen, adopts it as the active location, clearing any
  // selected zone so later "load more" / "save search" actions don't reuse the
  // stale zone.
  const applyViewport = useCallback(
    async (coordinates: GeoPoint[]) => {
      const status = await runSearch(searchState.buildSearchPayload({ vertices: coordinates }));
      // A failed or superseded pan (e.g. a rapid pan replaced by a newer one, or
      // a saved-search selection mid-flight) must not strand the URL, list, or
      // active location on a viewport whose results never applied — that would
      // leave later load-more / save-search actions using a polygon the visible
      // results and URL don't match.
      if (status !== 'applied') {
        return;
      }

      const queryString = buildVerticesQueryParams(coordinates);
      window.history.replaceState({}, '', `${window.location.pathname}?${queryString}`);
      updateQueryParams(searchState.getCanonical());

      searchState.resetPage();
      setVertices(coordinates);
      setSelectedLocation(undefined);
    },
    [searchState, runSearch],
  );

  // Appends the next page of list results for the current search.
  const loadMore = useCallback(async () => {
    if (!propertyListData?.nextPage) {
      return;
    }
    const nextPage = searchState.get.pageNumber + 1;
    const status = await loadNextPage(
      searchState.buildSearchPayload(currentSearchLocation),
      nextPage,
    );
    // Only advance the page counter when a page was actually appended; on a
    // failed fetch the hook flags the error and the "see more" affordance stays
    // so the user can retry.
    if (status === 'appended') {
      searchState.set.setPageNumber(nextPage);
    }
  }, [propertyListData?.nextPage, searchState, currentSearchLocation, loadNextPage]);

  // Applies a saved search selected from the dropdown. A same-market,
  // market-level search is applied in place (refetch + update filters + rewrite
  // the URL) so the user stays in their session. Zone/polygon searches and
  // cross-market switches need fresh SSR data (market shape, coordinates, zone
  // resolution), so we hard-navigate for those — the long-lived page never
  // re-hydrates its mount-time state from new props, so a soft route change
  // would leave results stale.
  const applySavedSearch = useCallback(
    async (savedSearch: SavedSearchFieldsFragment) => {
      const query = toPropertySearchQuery(savedSearch);
      const definedQuery = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined),
      );

      if (!canApplySavedSearchInPlace(savedSearch, marketInfo.identifier)) {
        const params = buildQueryParams(definedQuery, { seedFromUrl: false });
        window.location.assign(`${pickPathname(savedSearch)}?${params.toString()}`);
        return;
      }

      // Refetch from the saved query directly first (the state setters below
      // won't be visible to `buildSearchPayload` this tick). Only commit the
      // in-place UI/URL changes once we actually have results — a failed or
      // superseded fetch must not leave the page showing stale listings under
      // the saved-search filters and URL.
      const status = await runSearch(buildPayloadFromQuery(query, { market: marketInfo.name }));
      if (status !== 'applied') {
        return;
      }

      searchState.applyCanonical(query);
      setSelectedLocation(undefined);
      setSelectedAddress(undefined);
      setVertices(marketInfo.shape.coordinates[0]);
      searchState.resetPage();

      // Rewrite the URL to the canonical market path explicitly. Reusing the
      // current pathname would strand a stale zone route (e.g. `/homes/zid_x`)
      // that re-SSRs the old zone search on reload/share/back.
      const params = buildQueryParams(definedQuery, { seedFromUrl: false });
      window.history.replaceState({}, '', `/homes/${marketInfo.identifier}?${params.toString()}`);
    },
    [marketInfo, searchState, runSearch],
  );

  // The canonical query handed to the save-search button. `getCanonical()`
  // omits location, so the active market/zone/polygon is merged back in here.
  // We persist the routing slug (`identifier`), never the display `name`, so a
  // zone page's label isn't written into the saved search and later routed to a
  // non-existent `/homes/<label>`.
  const getSaveSearchQuery = useCallback(
    (): PropertySearchQuery =>
      buildSaveSearchQuery({
        canonical: searchState.getCanonical(),
        marketIdentifier: marketInfo.identifier,
        selectedZoneId: selectedLocation?.value,
        vertices,
      }),
    [searchState, marketInfo.identifier, selectedLocation?.value, vertices],
  );

  return {
    propertySearchData,
    propertyListData,
    setPropertyListData,
    isLoading,
    setIsLoading,
    hasError,
    clearError,
    vertices,
    setVertices,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyViewport,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
  };
};
